import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DialogButton, DialogContent } from "../../../components/dialog/Dialog";
import { useDialog } from "../../../components/dialog/DialogContext";
import { STANDARD_NUMBER_OF_BITS } from "../../../constants/credential-constants";
import TagStatus from '../../../constants/tag-status';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ButtonTypes } from "../../../models/ButtonTypes";
import { EnrolmentDevice } from "../../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../../models/EnrolmentTask";
import { PluginCommand } from "../../../models/PluginCommand";
import { Tag } from "../../../models/Tag";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import enrollCmd from "../../../services/wsPluginService/plugins/base/enrollCmd";
import { IShowEnrollValidOptions } from "../../../services/wsPluginService/plugins/base/pluginModels";
import { IPluginCard, IPluginCards } from "../../../services/wsPluginService/plugins/card/cardModels";
import { decodeMDEPluginMsg } from "../../../services/wsPluginService/plugins/card/mdePlugin";
import { decodeOmnikeyPluginMsg } from "../../../services/wsPluginService/plugins/card/omnikeyPlugin";
import { getEnrollResultCommandType } from "../../../services/wsPluginService/plugins/decodePluginMsg";
import Wizard from "../../wizard/wizard";
import DetailsForm from "../details-form";
import SubTasksForm from "../sub-tasks-form";
import SummaryForm from "./summary-form";
import { getTagTypes } from "@/constants/tag-type";

interface Props {
    availablePlugins: IShowEnrollValidOptions;
    availableSystemDeviceIds: number[];
    cardInfo: Tag;
    onClose: () => void;
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onBack: (event: any) => void;
    websocket: WebSocket | undefined;
    onSave: (values: Tag) => void;
}

const EnrolCardWizard: FC<Props> = ({ availablePlugins, cardInfo, onClose, defaultContent, defaultSteps, onBack, websocket, onSave, availableSystemDeviceIds }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const dialog = useDialog();
    const [cardValues, setCardValues] = useState<Tag>(cardInfo);
    const [taskDescription, setTaskDescription] = useState('');
    const [dualCard, setDualCard] = useState<any>(undefined);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const enrolmentTask = new EnrolmentTask(translate);

    // Handle Plugin Manager messages
    const onMessage = (value) => {
        if (value !== undefined) {
            if (getEnrollResultCommandType(value) === enrollCmd.CMD_MDE) {
                const mdeCards = decodeMDEPluginMsg(value) as IPluginCards;

                // Card details available
                if (!!mdeCards && !!mdeCards.cards && mdeCards.cards.length > 0) {

                    // Dual Card: user to choose between High and Low card to enroll
                    if (mdeCards.cards[0].cardNumber && mdeCards.cards[1].cardNumber) {
                        setDualCard(mdeCards);

                        // Buttons to show in Dual Card toast
                        const dialogButtons: DialogButton[] = [
                            {
                                id: "btn_high",
                                onClick: () => { showDualCardPortion(mdeCards.cards[1]) },
                                text: getCardTypeLabel(mdeCards.cards[1].cardType),
                                type: ButtonTypes.BlueRoundedButton
                            },
                            {
                                id: "btn_low",
                                onClick: () => { showDualCardPortion(mdeCards.cards[0]) }, // Empty onClick will just close the dialog
                                text: getCardTypeLabel(mdeCards.cards[0].cardType),
                                type: ButtonTypes.GreenRoundedButton,
                                className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                            }
                        ];

                        // Show the dual card message, ask user to choose which card to enroll
                        const lowCard = getCardTypeLabel(mdeCards.cards[0].cardType) + ': ' + mdeCards.cards[0].cardNumber;
                        const highCard = getCardTypeLabel(mdeCards.cards[1].cardType) + ': ' + mdeCards.cards[1].cardNumber;
                        const toastMessage = translate.byKeyFormatted(TranslationKeys.dualCardDetected, [lowCard, highCard]);
                        const dialogContent: DialogContent = {
                            title: translate.byKey(TranslationKeys.dualCard),
                            message: toastMessage,
                            buttons: dialogButtons
                        }
                        dialog.pushInfo(dialogContent);
                    }
                    // Low card found
                    else if (mdeCards.cards[0].cardNumber !== undefined) {
                        setCardValues({
                            ...cardValues,
                            code: mdeCards.cards[0].cardNumber,
                            tagDisplay: mdeCards.cards[0].cardNumber,
                            type: getCardTypeLabel(mdeCards.cards[0].cardType),
                            typeId: getCardTypeId(mdeCards.cards[0].cardType)
                        }) // for now just select first one
                    }
                    // High card found
                    else if (mdeCards.cards[1].cardNumber !== undefined) {
                        setCardValues({
                            ...cardValues,
                            code: mdeCards.cards[1].cardNumber,
                            tagDisplay: mdeCards.cards[1].cardNumber,
                            type: getCardTypeLabel(mdeCards.cards[1].cardType),
                            typeId: getCardTypeId(mdeCards.cards[1].cardType)
                        })
                    }
                    // No card number found
                    else {
                        if (mdeCards.cards[0].cardNumber !== undefined) {
                            setCardValues({
                                ...cardValues,
                                code: mdeCards.cards[0].cardNumber,
                                tagDisplay: mdeCards.cards[0].cardNumber,
                                type: getCardTypeLabel(mdeCards.cards[0].cardType),
                                typeId: getCardTypeId(mdeCards.cards[0].cardType)
                            })
                        }
                        else if (mdeCards.cards[1].cardNumber !== undefined) {
                            setCardValues({
                                ...cardValues,
                                code: mdeCards.cards[1].cardNumber,
                                tagDisplay: mdeCards.cards[1].cardNumber,
                                type: getCardTypeLabel(mdeCards.cards[1].cardType),
                                typeId: getCardTypeId(mdeCards.cards[1].cardType)
                            })
                        }
                        else {
                            toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                            onLocalBack();
                        }
                    }
                }
                // No card found
                else {
                    toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                    onLocalBack();
                }
            }
            else if (getEnrollResultCommandType(value) === enrollCmd.CMD_OMNIKEY) {
                const omnikeyCard = decodeOmnikeyPluginMsg(value) as IPluginCard;
                const seosType = 'Seos'

                // Desfire has a larger hex value. We can't rely on parseInt which would truncate the value, so we use BigInt instead.
                const code = BigInt("0x" + omnikeyCard.cardHex).toString();

                if (omnikeyCard.cardNumber !== "0") {
                    const card = {
                        ...cardValues,
                        type: getCardTypeLabel(omnikeyCard.cardType),
                        code: code,
                        tagDisplay: code,
                        status: TagStatus.Normal.value,
                        typeId: getCardTypeId(omnikeyCard.cardType),
                        numberOfBits: omnikeyCard.formatType.includes(seosType) ? omnikeyCard.cardNoBits : STANDARD_NUMBER_OF_BITS,
                        tagByteReversed: omnikeyCard.reversedBytes
                    }
                    setCardValues(card);
                }
                else {
                    toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                    onLocalBack();
                }
            }
        }
    }

    /*
     User read a dual card on MDE or Omnikey.
     Show the portion of the card they chose to enroll.
    */
    const showDualCardPortion = (card: IPluginCard) => {
        setCardValues({
            ...cardValues,
            code: card.cardNumber,
            tagDisplay: card.cardNumber,
            type: getCardTypeLabel(card.cardType),
            typeId: getCardTypeId(card.cardType)
        })
    }

    const getCardType = (cardType: string) => {
        let type = getTagTypes().Slim; // Default to Slim for all other types.

        if (cardType === getTagTypes().Mifare.value) {
            type = getTagTypes().Mifare;
        }
        else if (cardType === getTagTypes().General.value) {
            type = getTagTypes().General;
        }
        else if (cardType === getTagTypes().HID.value) {
            type = getTagTypes().HID;
        }
        else if (cardType === getTagTypes().Felica.value) {
            type = getTagTypes().Felica;
        }
        else if (cardType === getTagTypes().WriTag_2048.value) {
            type = getTagTypes().WriTag_2048;
        }
        else if (cardType === getTagTypes().WriTag_128.value) {
            type = getTagTypes().WriTag_128;
        }

        return type;
    }

    const getCardTypeLabel = (cardType: string) => {
        return getCardType(cardType).label;
    }

    const getCardTypeId = (cardType: string) => {
        return getCardType(cardType).value;
    }

    useEffect(() => {
        if (websocket && websocket?.readyState === WebSocket.OPEN) {
            websocket.onmessage = (value) => {
                onMessage(value.data)
            };
        }
    }, [])

    const onLocalNext = (event) => {
        // MDE / OMNIKEY
        if (event.task && (event.task === enrolmentDevice.MDE.value || event.task === enrolmentDevice.OMNIKEY.value)) {
            requestToEnrol(event.task);
            setTaskDescription(event.description);
        }
        // Random PAC 
        else if (event.task && event.task === enrolmentDevice.RANDOM_PAC.value) {
            const PAC_TAG_TYPE_ID: string = "7";
            const MAX_TAG_CODE: number = 99999;
            const code = (Math.floor(Math.random() * MAX_TAG_CODE) + 1).toString();

            setCardValues({
                ...cardValues,
                status: TagStatus.Normal.value,
                typeId: PAC_TAG_TYPE_ID,
                type: translate.byKey(TranslationKeys.personalAccesssCode),
                code: code,
                tagDisplay: code,
                numberOfBits: STANDARD_NUMBER_OF_BITS
            })

            setTaskDescription(event.description);
        }
        // Manual entry / Vingcard
        else if (event.task && event.task === enrolmentDevice.MANUAL_CODE_TYPE.value) {
            setCardValues(cardInfo);
            setTaskDescription(event.description);
        }

        setStep(step + 1);
    }

    const requestToEnrol = (task) => {
        let command = '';
        const mdeDevice: PluginCommand = enrolmentDevice.MDE;
        const omnikeyDevice: PluginCommand = enrolmentDevice.OMNIKEY;

        switch (task) {
            case mdeDevice.value:
                command = !mdeDevice.command ? '' : mdeDevice.command;
                break;
            case omnikeyDevice.value:
                command = !omnikeyDevice.command ? '' : omnikeyDevice.command;
                break;
            default:
                break;
        }

        if (command !== '') {
            websocket?.send(command); // set message to send
        }
    }

    const onLocalBack = (event?: any) => {
        if (step === 1) {
            onBack(event);
            return;
        } else if (step === 2) {
            setTaskDescription("");
        }

        setStep(step - 1);
    }

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: taskDescription,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.enterDetails),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const content = [
        ...defaultContent,
        <SubTasksForm
            onNext={(event) => onLocalNext(event)}
            pluginsAvailable={availablePlugins}
            onBack={(event) => onLocalBack(event)}
            enrollMethod={enrolmentTask.ENROL_CARD.value}
            availableSystemDeviceIds={availableSystemDeviceIds} />,
        <DetailsForm
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            cardInfo={cardValues} />,
        <SummaryForm
            taskDescription={steps[0].description}
            taskProcedure={taskDescription}
            onBack={(event) => onLocalBack(event)}
            isEdit={false} />,

    ];

    return <Formik
        enableReinitialize={true}
        initialValues={cardValues}
        onSubmit={onSave}>
        <Form>
            <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                step={step}
                onClose={() => onClose()}
                sidebarSteps={steps}>
                {content}
            </Wizard>
        </Form>
    </Formik>
}

export default EnrolCardWizard;
